import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Badge,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function AllOrdersBilling() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [clinicId, setClinicId] = useState('');

  const params = {
    startDate,
    endDate,
    ...(clinicId ? { clinicId } : {}),
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'billing', startDate, endDate, clinicId],
    queryFn: () => reportsApi.getBilling(params),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/billing', params);
    } finally {
      setExporting(false);
    }
  };

  const rows = (data?.data || []).map((item) => [
    <Button key={item.rentalId} variant="plain" onClick={() => navigate(`/rentals/${item.rentalId}`)}>
      {item.orderNumber || item.rentalId.slice(0, 8)}
    </Button>,
    formatDate(item.createdAt),
    item.clinicName || 'Direct',
    <Badge key={`type-${item.rentalId}`}>{item.orderType}</Badge>,
    item.customerName || '—',
    formatDate(item.startDate),
    formatDate(item.endDate),
    formatCurrency(item.subtotal),
    formatCurrency(item.tax),
    formatCurrency(item.shipping),
    formatCurrency(item.total),
    item.status,
    item.invoiceStatus || '—',
  ]);

  return (
    <Page
      title="All Orders Billing"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <TextField
              label="Clinic ID (optional)"
              value={clinicId}
              onChange={setClinicId}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the All Orders Billing report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No orders found for the selected date range.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <InlineGrid columns={2} gap="400">
                <StatCard label="Total Orders" value={String(data.summary.totalOrders)} />
                <StatCard label="Total Revenue" value={formatCurrency(data.summary.totalRevenue)} />
              </InlineGrid>

              <DataTable
                columnContentTypes={[
                  'text', 'text', 'text', 'text', 'text', 'text', 'text',
                  'text', 'text', 'text', 'text', 'text', 'text',
                ]}
                headings={[
                  'Order', 'Created', 'Clinic', 'Type', 'Customer',
                  'Rental Start', 'Rental End', 'Subtotal', 'Tax', 'Shipping',
                  'Total', 'Status', 'Invoice',
                ]}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
